'use client';;
import React, { useEffect, useReducer, useMemo, useRef, ReactNode } from 'react';
import { cartStorage } from '../storage/cartStorage';
import { gatewayClient } from '../api';
import { CartDocument } from '../types/cartApi';
import { Card } from '../types/card';
import { createCtx } from '../utils/createContext';
import { useCurrencyContext } from './CurrencyContext';
import { useAuth } from './AuthContext';
import { ApiError } from '../api/errors';

// Define cart context interface
interface CartContextType {
  cartId: string | null;
  cart: CartDocument | null;
  cards: Card[];
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
  clearError: () => void;

  addToCart: (productId: string, quantity: number, selectedValue: number, currency: string, country?: string, metaEventId?: string) => Promise<void>;
  updateItemQuantity: (productId: string, quantity: number, productIndex: number) => Promise<void>;
  updateItemValue: (productId: string, selectedValue: number, productIndex: number) => Promise<void>;
  removeFromCart: (productIndex: number) => Promise<void>;
  clearCart: () => Promise<void>;

  getTotalItems: () => number;
  getTotalPrice: () => number;

  loadCart: (cartId: string) => Promise<void>;
  adoptCart: (cartId: string) => Promise<void>;
  createNewCart: () => void;
  mergeCartOnLogin: () => Promise<void>;
}

// Create context using our utility
const [CartContextProvider, useCartContextHook] = createCtx<CartContextType>();

// Define cart state interface
interface CartState {
  cartId: string | null;
  cart: CartDocument | null;
  cards: Card[];
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
}

// Define action types
type CartAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: { message: string | null; code?: string | null } }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_CART_ID'; payload: string | null }
  | { type: 'SET_CART'; payload: CartDocument }
  | { type: 'SET_CARDS'; payload: Card[] }
  | { type: 'CLEAR_CART' };

// Cart reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload.message, errorCode: action.payload.code || null, isLoading: false };

    case 'CLEAR_ERROR':
      return { ...state, error: null, errorCode: null };

    case 'SET_CART_ID':
      return { ...state, cartId: action.payload };

    case 'SET_CART':
      return { ...state, cart: action.payload, isLoading: false, error: null };

    case 'SET_CARDS':
      return { ...state, cards: action.payload };

    case 'CLEAR_CART':
      return { ...state, cartId: null, cart: null, cards: [], isLoading: false, error: null, errorCode: null };

    default:
      return state;
  }
};

/**
 * Check if error indicates cart should be cleared from local storage
 * Handles: cart not found, cart with existing order
 */
const shouldClearCart = (error: unknown): boolean => {
  const apiError = error as {
    status?: number;
    statusCode?: number;
    errorCode?: string;
    message?: string;
  };
  const statusCode = apiError?.status || apiError?.statusCode;
  const errorCode = apiError?.errorCode;
  const errorMessage = (apiError?.message || String(error)).toLowerCase();

  // Cart not found - cart was deleted or never existed. The API responds
  // with a bare errorCode (no message), so match the code first and keep the
  // legacy message match as a fallback.
  if (
    errorCode === 'CART_NOT_FOUND' ||
    (statusCode === 404 && errorMessage.includes('cart not found'))
  ) {
    return true;
  }

  // Cart locked by an existing order
  if (
    errorCode === 'CART_LOCKED_BY_ORDER' ||
    (statusCode === 404 &&
      errorMessage.includes('cannot modify cart with an existing order'))
  ) {
    return true;
  }

  return false;
};

// Keep old function name for backwards compatibility
const isCartWithExistingOrderError = shouldClearCart;

/**
 * Fetch cards for cart items using getCardsByIds
 */
const fetchCardsForCart = async (cart: CartDocument): Promise<Card[]> => {
  if (!cart.products || cart.products.length === 0) {
    return [];
  }

  try {
    const productIds = cart.products.map(item => item.productId);
    const cards = await gatewayClient.cards.getCardsByIds(productIds);
    return cards;
  } catch (error) {
    console.error('Failed to fetch cards:', error);
    return [];
  }
};

/**
 * CartProvider - Provides cart state and operations to the app
 * Now works with backend API instead of local storage
 */
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get currency context to update selectedCurrency when adding first item
  const { updateCurrency } = useCurrencyContext();
  const { user } = useAuth();

  // Initialize state
  const [state, dispatch] = useReducer(cartReducer, {
    cartId: null,
    cart: null,
    cards: [],
    isLoading: false,
    error: null,
    errorCode: null
  });

  const clearError = React.useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Concurrent loads race: on /cart?id= landings both the storage hydration
  // below and the URL adoption fire, and "last HTTP response wins" displayed
  // whichever cart happened to resolve later. The sequence guard makes the
  // last-INITIATED load authoritative; adoptedCartRef lets a URL adoption
  // suppress the storage hydration entirely.
  const loadSeqRef = useRef(0);
  const adoptedCartRef = useRef(false);

  // Load cart from API
  const loadCart = React.useCallback(async (cartId: string) => {
    const seq = ++loadSeqRef.current;
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartData = await gatewayClient.cart.getCartById(cartId);
      if (seq !== loadSeqRef.current) return; // superseded by a newer load
      // Adopt the loaded cart as the active one - URL recovery links (email,
      // agent-created carts) must survive into checkout, not just render.
      if (cartData._id) {
        dispatch({ type: 'SET_CART_ID', payload: cartData._id });
        cartStorage.saveCartId(cartData._id);
      }
      dispatch({ type: 'SET_CART', payload: cartData });
      // Fetch cards for cart items
      const cards = await fetchCardsForCart(cartData);
      if (seq !== loadSeqRef.current) return;
      dispatch({ type: 'SET_CARDS', payload: cards });
    } catch (error) {
      if (seq !== loadSeqRef.current) return;
      console.error('Failed to load cart:', error);
      if (isCartWithExistingOrderError(error)) {
        console.log('Cart has existing order, clearing cartId from storage');
        dispatch({ type: 'CLEAR_CART' });
        cartStorage.clearCartId();
        return;
      }
      dispatch({ type: 'SET_ERROR', payload: { message: 'Failed to load cart' } });
    }
  }, []);

  // Explicit adoption from a /cart?id= link (email recovery, agent carts).
  // The URL cart must win over any cart this browser already had.
  const adoptCart = React.useCallback(async (cartId: string) => {
    adoptedCartRef.current = true;
    await loadCart(cartId);
  }, [loadCart]);

  // Load cart ID from storage on mount - unless a URL adoption already
  // claimed this session's cart (direct /cart?id= landings: the page's
  // effect runs before this provider effect). Adoptions that start later
  // (SPA navigation) win anyway via the sequence guard.
  useEffect(() => {
    if (adoptedCartRef.current) return;
    const savedCartId = cartStorage.getCartId();
    if (savedCartId) {
      dispatch({ type: 'SET_CART_ID', payload: savedCartId });
      loadCart(savedCartId);
    }
  }, [loadCart]);

  // Clear the local cart pointer on logout (authed → unauthed transition).
  // The ref guard stops this firing on initial mount, when `user` is briefly
  // null before AuthContext hydrates from localStorage. Pure UX: the cart
  // itself still lives on the server and reattaches on next login via merge.
  const prevUserRef = useRef(user);
  useEffect(() => {
    if (prevUserRef.current && !user) {
      dispatch({ type: 'CLEAR_CART' });
      cartStorage.clearCartId();
    }
    prevUserRef.current = user;
  }, [user]);

  // Add item to cart
  const addToCart = React.useCallback(async (productId: string, quantity: number, selectedValue: number, currency: string, country?: string, metaEventId?: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const isEmptyCart = !state.cartId || !state.cart || !state.cart.products || state.cart.products.length === 0;
      const cartData = await gatewayClient.cart.addToCart({
        cartId: state.cartId || undefined,
        productId,
        quantity,
        selectedValue,
        currency,
        country,
        ...(metaEventId && { metaEventId }),
      });
      // Adopt the returned cart id whenever it differs - the backend creates
      // a fresh cart when the one we sent no longer exists.
      if (cartData._id && cartData._id !== state.cartId) {
        dispatch({ type: 'SET_CART_ID', payload: cartData._id });
        cartStorage.saveCartId(cartData._id);
      }
      dispatch({ type: 'SET_CART', payload: cartData });
      const cards = await fetchCardsForCart(cartData);
      dispatch({ type: 'SET_CARDS', payload: cards });
      // The first item sets the display currency: the one it is sold in.
      if (isEmptyCart && cards.some(card => card._id === productId)) {
        updateCurrency(currency);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      if (isCartWithExistingOrderError(error)) {
        console.log('Cart has existing order, clearing cartId and retrying add to cart');
        dispatch({ type: 'CLEAR_CART' });
        cartStorage.clearCartId();
        throw error;
      }
      const apiError = error as ApiError;
      dispatch({ type: 'SET_ERROR', payload: { message: apiError.message || 'Failed to add item to cart', code: apiError.errorCode } });
      throw error;
    }
  }, [state.cartId, state.cart, updateCurrency]);

  // Update item quantity
  const updateItemQuantity = React.useCallback(async (productId: string, quantity: number, productIndex: number) => {
    if (!state.cartId) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartData = await gatewayClient.cart.updateCartItem({
        cartId: state.cartId,
        productId,
        quantity,
        productIndex
      });

      dispatch({ type: 'SET_CART', payload: cartData });

      // Fetch cards for cart items
      const cards = await fetchCardsForCart(cartData);
      dispatch({ type: 'SET_CARDS', payload: cards });
    } catch (error) {
      console.error('Failed to update quantity:', error);

      // Check if this is the specific error for cart with existing order
      if (isCartWithExistingOrderError(error)) {
        console.log('Cart has existing order, clearing cartId from storage');
        dispatch({ type: 'CLEAR_CART' });
        cartStorage.clearCartId();
        return;
      }

      const apiError = error as ApiError;
      dispatch({ type: 'SET_ERROR', payload: { message: apiError.message || 'Failed to update item quantity', code: apiError.errorCode } });
    }
  }, [state.cartId]);

  // Update item value
  const updateItemValue = React.useCallback(async (productId: string, selectedValue: number, productIndex: number) => {
    if (!state.cartId) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartData = await gatewayClient.cart.updateCartItem({
        cartId: state.cartId,
        productId,
        selectedValue,
        productIndex
      });

      dispatch({ type: 'SET_CART', payload: cartData });

      // Fetch cards for cart items
      const cards = await fetchCardsForCart(cartData);
      dispatch({ type: 'SET_CARDS', payload: cards });
    } catch (error) {
      console.error('Failed to update value:', error);

      // Check if this is the specific error for cart with existing order
      if (isCartWithExistingOrderError(error)) {
        console.log('Cart has existing order, clearing cartId from storage');
        dispatch({ type: 'CLEAR_CART' });
        cartStorage.clearCartId();
        return;
      }

      const apiError = error as ApiError;
      dispatch({ type: 'SET_ERROR', payload: { message: apiError.message || 'Failed to update item value', code: apiError.errorCode } });
    }
  }, [state.cartId]);

  // Remove item from cart
  const removeFromCart = React.useCallback(async (productIndex: number) => {
    console.log('WTF', productIndex)

    if (!state.cartId) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartData = await gatewayClient.cart.removeFromCart({
        cartId: state.cartId,
        productIndex
      });

      dispatch({ type: 'SET_CART', payload: cartData });

      // Fetch cards for cart items
      const cards = await fetchCardsForCart(cartData);
      dispatch({ type: 'SET_CARDS', payload: cards });
    } catch (error) {
      console.error('Failed to remove from cart:', error);

      // Check if this is the specific error for cart with existing order
      if (isCartWithExistingOrderError(error)) {
        console.log('Cart has existing order, clearing cartId from storage');
        dispatch({ type: 'CLEAR_CART' });
        cartStorage.clearCartId();
        return;
      }

      dispatch({ type: 'SET_ERROR', payload: { message: 'Failed to remove item from cart' } });
    }
  }, [state.cartId]);

  // Clear cart
  const clearCart = React.useCallback(async () => {
    if (!state.cartId) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await gatewayClient.cart.emptyCart(state.cartId);
      dispatch({ type: 'CLEAR_CART' });
      cartStorage.clearCartId();
    } catch (error) {
      console.error('Failed to clear cart:', error);
      dispatch({ type: 'SET_ERROR', payload: { message: 'Failed to clear cart' } });
    }
  }, [state.cartId]);

  // Create new cart (reset state)
  const createNewCart = React.useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    cartStorage.clearCartId();
  }, []);

  /**
   * Call after a successful login/register-confirm/google-auth.
   * Backend merges guest + pending carts server-side; we just swap our cartId
   * to whatever the server returned. Errors are logged but never thrown -
   * a failed merge must not block the post-login redirect.
   */
  const mergeCartOnLogin = React.useCallback(async () => {
    try {
      // Read live - on confirm-email / google-callback landings the hydration
      // effect above may not have populated `state.cartId` yet.
      const currentGuestId = cartStorage.getCartId() ?? undefined;
      const merged = await gatewayClient.cart.mergeCart(currentGuestId);

      if (!merged || !merged._id) {
        // User has no guest cart and no pending carts - clear everything so the
        // next addToCart creates a fresh one.
        dispatch({ type: 'CLEAR_CART' });
        cartStorage.clearCartId();
        return;
      }

      cartStorage.saveCartId(merged._id);
      dispatch({ type: 'SET_CART_ID', payload: merged._id });
      dispatch({ type: 'SET_CART', payload: merged });

      const cards = await fetchCardsForCart(merged);
      dispatch({ type: 'SET_CARDS', payload: cards });
    } catch (error) {
      // Swallow - 403/409/network blip must not block redirect.
      console.error('Cart merge on login failed:', error);
    }
  }, []);

  // Memoize computations
  const getTotalItems = React.useCallback(() => {
    return state.cart?.products?.reduce((total, item) => total + item.quantity, 0) || 0;
  }, [state.cart]);

  const getTotalPrice = React.useCallback(() => {
    return state.cart?.products?.reduce((total, item) => {
      return total + (item.selectedValue * item.quantity);
    }, 0) || 0;
  }, [state.cart]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    cartId: state.cartId,
    cart: state.cart,
    cards: state.cards,
    isLoading: state.isLoading,
    error: state.error,
    errorCode: state.errorCode,
    clearError,
    addToCart,
    updateItemQuantity,
    updateItemValue,
    removeFromCart,
    clearCart,
    getTotalItems,
    getTotalPrice,
    loadCart,
    adoptCart,
    createNewCart,
    mergeCartOnLogin
  }), [
    state.cartId,
    state.cart,
    state.cards,
    state.isLoading,
    state.error,
    state.errorCode,
    clearError,
    addToCart,
    updateItemQuantity,
    updateItemValue,
    removeFromCart,
    clearCart,
    getTotalItems,
    getTotalPrice,
    loadCart,
    adoptCart,
    createNewCart,
    mergeCartOnLogin
  ]);

  return (
    <CartContextProvider value={contextValue}>
      {children}
    </CartContextProvider>
  );
};

// Export a custom hook for using cart context
export const useCart = () => useCartContextHook('useCart'); 